/**
 * @file 邮箱账户管理页（Email Accounts）
 *
 * 该文件定义了 CRM 系统的邮箱账户管理组件 `CrmEmailAccountsPage`。
 * 主要功能包括：
 * - 邮箱账户列表展示（表格形式，含地址、POP3/IMAP/SMTP 服务器、所有者、同步状态、状态）
 * - 新增/编辑邮箱账户（模态框表单，含地址、密码、POP3/IMAP/SMTP 服务器配置）
 * - 密码可见性切换
 * - 连接测试（支持列表行内测试和模态框内测试）
 * - 同步开关切换和启用/禁用状态切换
 * - 签名管理（通过独立的签名编辑器模态框）
 * - 邮件同步任务触发
 * - 删除账户（带确认对话框）
 * - 分页导航
 *
 * 数据来源：`emailAccountApi`（账户 CRUD）、`userApi`（用户名映射）、`taskApi`（邮件同步任务）
 *
 * 设计决策：
 * - 使用 `repeat` 指令优化列表渲染性能（基于账户 ID 作为 key）
 * - 管理员可见「所有者」列（通过 isAdmin 标志控制）
 * - 支持 ESC 键关闭模态框
 * - 模态框背景点击关闭（mousedown + click 模式避免误触）
 * - 同步和状态切换使用局部更新（仅替换变更行），避免整列表重新渲染
 */

import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { emailAccountApi, userApi, taskApi } from '../api.js';
import { BaseElement } from '../base-element.js';
import type { EmailAccount, CreateEmailAccountRequest, UpdateEmailAccountRequest } from '../types.js';
import { toast } from '../components/toast.js';
import '../components/table-toolbar.js';
import '../components/loading-overlay.js';
import '../components/signature-editor.js';
import '../components/pagination.js';
import { showConfirm } from '../components/confirm-dialog.js';
import { isNetworkError } from '../utils/index.js';

/**
 * 邮箱账户管理页组件
 *
 * 继承 `BaseElement`，注册为 `<crm-email-accounts-page>` 自定义元素。
 * 提供邮箱账户的增删改查、连接测试、同步控制、签名管理等功能。
 *
 * 状态字段：
 * - `accounts` — 当前页邮箱账户列表
 * - `isLoading` — 加载状态
 * - `total` / `currentPage` / `perPage` — 分页参数
 * - `showModal` — 账户编辑模态框开关
 * - `editingAccount` — 当前编辑的账户对象，null 时为新增模式
 * - `showPassword` — 密码输入框可见性切换
 * - `userMap` — 用户 ID 到用户名的映射（用于显示账户所有者）
 * - `isAdmin` — 当前用户是否为管理员（控制「所有者」列显示）
 * - `testingId` — 正在测试连接的账户 ID（用于显示加载动画）
 * - `signatureModalAccountId` — 签名编辑器模态框对应的账户 ID
 */
@customElement('crm-email-accounts-page')
export class CrmEmailAccountsPage extends BaseElement {
  /** 当前页邮箱账户列表 */
  @state()
  private accounts: EmailAccount[] = [];

  /** 加载状态标志 */
  @state()
  private isLoading = false;
  /** 列表请求序号，仅允许最后一次分页请求更新页面。 */
  private _listRequestId = 0;

  /** 账户总数，用于分页 */
  @state()
  private total = 0;

  /** 当前页码 */
  @state()
  private currentPage = 1;

  /** 每页条数 */
  @state()
  private perPage = 20;

  /** 是否显示账户编辑模态框 */
  @state()
  private showModal = false;

  /** 当前编辑的账户对象，null 时为新增模式 */
  @state()
  private editingAccount: EmailAccount | null = null;

  /** 密码输入框是否以明文显示 */
  @state()
  private showPassword = false;

  /** 用户 ID → 用户名的映射，用于显示账户所有者 */
  @state()
  private userMap: Map<number, string> = new Map();

  /** 当前用户是否为管理员（管理员可见「所有者」列） */
  @state()
  private isAdmin = false;

  /** 正在测试连接的账户 ID，用于行内显示加载动画 */
  @state()
  private testingId: number | null = null;

  /** 签名编辑器模态框对应的账户 ID，null 时模态框不显示 */
  @state()
  private signatureModalAccountId: number | null = null;

  /** 组件样式：撑满容器高度 */
  static styles = css`
    :host { display: block; height: 100%; }
  `;

  /**
   * 组件挂载到 DOM 后的生命周期回调
   *
   * 调用父类方法后：
   * 1. 加载邮箱账户列表
   * 2. 加载用户名映射（同时检测管理员权限）
   * 3. 注册全局 ESC 键监听器用于关闭模态框
   */
  connectedCallback() {
    super.connectedCallback();
    this.loadAccounts();
    this.loadUserNames();
    // 注册 ESC 键监听，使模态框可通过按 Escape 键关闭
    document.addEventListener('keydown', this.handleEscKey);
  }

  /**
   * 组件从 DOM 移除时的生命周期回调
   *
   * 移除全局 ESC 键监听器，防止内存泄漏。
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleEscKey);
  }

  /**
   * ESC 键事件处理器（箭头函数，保持 this 绑定）
   *
   * 当账户编辑模态框打开时按 ESC 键关闭模态框。
   * @param e - 键盘事件对象
   */
  private handleEscKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.showModal) {
      this.closeModal();
    }
  };

  /**
   * 加载用户名映射并检测管理员权限
   *
   * 调用 `userApi.list()` 获取所有用户，构建 ID→用户名的映射表。
   * 同时检查是否存在 admin 角色用户以设置 isAdmin 标志。
   *
   * @returns 无返回值（Promise<void>）
   */
  private async loadUserNames() {
    try {
      const response = await userApi.list();
      const map = new Map<number, string>();
      for (const user of response.data) {
        map.set(user.id, user.username);
        // 检测是否存在管理员用户以控制「所有者」列显示
        if (user.role === 'admin') this.isAdmin = true;
      }
      this.userMap = map;
    } catch {
      console.error('Failed to load user names');
    }
  }

  /**
   * 加载邮箱账户列表
   *
   * 根据当前分页参数调用 `emailAccountApi.list()` 获取数据。
   * 成功后更新账户列表、总数、页码和每页条数。
   *
   * @returns 无返回值（Promise<void>）
   */
  private async loadAccounts() {
    const requestId = ++this._listRequestId;
    this.isLoading = true;
    try {
      const response = await emailAccountApi.list({ page: this.currentPage, per_page: this.perPage });
      if (requestId !== this._listRequestId) return;
      this.accounts = response.data;
      this.total = response.pagination.total;
      this.currentPage = response.pagination.current_page;
      this.perPage = response.pagination.per_page;
    } catch {
      if (requestId === this._listRequestId) console.error('Failed to load accounts');
    } finally {
      if (requestId === this._listRequestId) this.isLoading = false;
    }
  }

  /**
   * 分页变更处理器
   * @param page - 目标页码
   */
  private handlePageChange(page: number) {
    this.currentPage = page;
    this.loadAccounts();
  }

  /** 打开新增账户模态框，重置编辑状态并隐藏密码 */
  private openCreateModal() { this.editingAccount = null; this.showPassword = false; this.showModal = true; }
  /** 打开编辑账户模态框，设置编辑目标并隐藏密码 */
  private openEditModal(account: EmailAccount) { this.editingAccount = account; this.showPassword = false; this.showModal = true; }
  /** 关闭账户模态框并清除编辑状态（箭头函数） */
  private closeModal = () => { this.showModal = false; this.editingAccount = null; }

  /**
   * 切换邮箱账户的邮件同步开关
   *
   * 调用 `emailAccountApi.toggleSync()` 切换同步状态，
   * 成功后局部更新对应账户的 sync_enabled 字段（避免整列表重新渲染）。
   * 失败时显示错误 toast（过滤网络错误）。
   *
   * @param account - 目标邮箱账户对象
   * @returns 无返回值（Promise<void>）
   */
  private async toggleAccountSync(account: EmailAccount) {
    // 计算目标状态：当前同步中则关闭(0)，当前关闭则开启(1)
    const next = account.sync_enabled ? 0 : 1;
    try {
      const result = await emailAccountApi.toggleSync(account.id, next);
      // 局部更新：仅替换被切换的账户行，避免整列表重渲染
      const index = this.accounts.findIndex(a => a.id === account.id);
      if (index !== -1) {
        this.accounts = [
          ...this.accounts.slice(0, index),
          { ...this.accounts[index], sync_enabled: result.sync_enabled },
          ...this.accounts.slice(index + 1),
        ];
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '操作失败';
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 切换邮箱账户的启用/禁用状态
   *
   * 调用 `emailAccountApi.toggleStatus()` 切换状态，
   * 成功后局部更新对应账户的 status 字段。
   * 失败时显示错误 toast（过滤网络错误）。
   *
   * @param account - 目标邮箱账户对象
   * @returns 无返回值（Promise<void>）
   */
  private async toggleAccountStatus(account: EmailAccount) {
    // 计算目标状态：当前启用则禁用(0)，当前禁用则启用(1)
    const next = account.status ? 0 : 1;
    try {
      const result = await emailAccountApi.toggleStatus(account.id, next);
      // 局部更新：仅替换被切换的账户行
      const index = this.accounts.findIndex(a => a.id === account.id);
      if (index !== -1) {
        this.accounts = [
          ...this.accounts.slice(0, index),
          { ...this.accounts[index], status: result.status },
          ...this.accounts.slice(index + 1),
        ];
      }
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '操作失败');
    }
  }

  /** 打开签名编辑器模态框，设置目标账户 ID */
  private openSignatureModal(accountId: number) { this.signatureModalAccountId = accountId; }
  /** 关闭签名编辑器模态框（箭头函数） */
  private closeSignatureModal = () => { this.signatureModalAccountId = null; }

  /**
   * 测试已保存邮箱账户的连接
   *
   * 调用 `emailAccountApi.testConnection()` 测试已保存账户的连接。
   * 使用 testingId 标记正在测试的账户以显示加载动画。
   * 成功/失败均通过 toast 提示结果（过滤网络错误）。
   *
   * @param account - 目标邮箱账户对象
   * @returns 无返回值（Promise<void>）
   */
  private async testConnection(account: EmailAccount) {
    this.testingId = account.id;
    try {
      const result = await emailAccountApi.testConnection(account.id);
      if (result.success) {
        toast.success(`${account.address}: 连接成功`);
      } else {
        toast.error(`${account.address}: ${result.message}`);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '测试失败';
      if (!isNetworkError(msg)) toast.error(msg);
    } finally {
      this.testingId = null;
    }
  }

  /**
   * 测试模态框中未保存表单的连接
   *
   * 编辑模式下直接测试已保存账户；新增模式下从表单读取临时数据
   * 调用 `emailAccountApi.testCredentials()` 测试未保存的凭据。
   * 需要填写邮箱地址、密码和 IMAP 服务器才能测试。
   *
   * @returns 无返回值（Promise<void>）
   */
  private async testModalConnection() {
    if (this.editingAccount) {
      // 编辑模式：直接测试已保存的账户
      await this.testConnection(this.editingAccount);
    } else {
      // 新增模式：从表单读取临时数据测试
      const form = this.shadowRoot?.querySelector('form') as HTMLFormElement;
      if (!form) return;
      const formData = new FormData(form);
      const address = formData.get('address') as string;
      const password = formData.get('password') as string;
      const imap = formData.get('imap') as string;
      if (!address || !password || !imap) {
        toast.error('请填写邮箱地址、密码和 IMAP 服务器');
        return;
      }
      try {
        const result = await emailAccountApi.testCredentials({ address, password, imap });
        toast[result.success ? 'success' : 'error'](result.success ? '连接成功' : result.message);
      } catch (err: any) {
        if (!isNetworkError(err.message)) toast.error(err.message || '测试失败');
      }
    }
  }

  /**
   * 邮箱账户表单提交处理器
   *
   * 从 FormData 收集表单数据（地址、POP3、IMAP、SMTP），
   * 根据编辑/新增模式分别调用 update/create API。
   * - 编辑模式：密码有值时才更新，留空不修改
   * - 新增模式：密码必填
   * 成功后重新加载列表并关闭模态框。
   *
   * @param e - 表单提交事件
   * @returns 无返回值（Promise<void>）
   */
  private async handleSubmit(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const data: Record<string, any> = {
      address: formData.get('address') as string,
      pop3: formData.get('pop3') as string,
      imap: formData.get('imap') as string,
      smtp: formData.get('smtp') as string,
      status: 1,
    };

    const password = formData.get('password') as string;
    if (this.editingAccount) {
      // 编辑模式：密码有值时才更新，留空不修改
      if (password) data.password = password;
      await emailAccountApi.update(this.editingAccount.id, data as UpdateEmailAccountRequest);
    } else {
      // 新增模式：密码必填
      if (!password) { toast.error('密码不能为空'); return; }
      data.password = password;
      await emailAccountApi.create(data as CreateEmailAccountRequest);
    }

    await this.loadAccounts();
    this.closeModal();
  }

  /**
   * 删除邮箱账户
   *
   * 弹出确认对话框，用户确认后调用 `emailAccountApi.delete()` 删除。
   * 成功后重新加载列表。失败时显示错误 toast（过滤网络错误）。
   *
   * @param id - 待删除的账户 ID
   * @returns 无返回值（Promise<void>）
   */
  private async deleteAccount(id: number) {
    if (!(await showConfirm('确定要删除此邮箱账户吗？'))) return;
    try { await emailAccountApi.delete(id); await this.loadAccounts(); } catch (err: unknown) { const msg = err instanceof Error ? err.message : '删除失败'; if (!isNetworkError(msg)) toast.error(msg); }
  }

  /**
   * 触发邮件同步任务
   *
   * 调用 `taskApi.fetchEmails()` 向 Arq 任务队列发送邮件同步任务。
   * 成功/失败均通过 toast 提示（过滤网络错误）。
   *
   * @returns 无返回值（Promise<void>）
   */
  private async syncEmails() {
    try {
      await taskApi.fetchEmails({});
      toast.success(`邮件同步任务已启动`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '同步失败';
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /** 模态框背景点击检测标志 */
  private _modalBackdropMousedown = false;

  /**
   * 渲染通用模态框容器
   *
   * 提供背景遮罩、标题栏、关闭按钮和内容容器。
   * 支持点击背景关闭（mousedown + click 模式避免误触）。
   * 支持自定义最大宽度参数。
   *
   * @param title - 模态框标题文本
   * @param body - 模态框内容（Lit TemplateResult）
   * @param onClose - 关闭回调函数
   * @param maxWidth - 最大宽度类名，默认 'max-w-lg sm:max-w-xl'
   * @returns Lit TemplateResult
   */
  private renderModal(title: string, body: any, onClose: () => void, maxWidth = 'max-w-lg sm:max-w-xl') {
    return html`
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50"
           @mousedown=${(e: Event) => { this._modalBackdropMousedown = e.target === e.currentTarget; }}
           @click=${(e: Event) => { if (e.target === e.currentTarget && this._modalBackdropMousedown) onClose(); this._modalBackdropMousedown = false; }}>
        <div class="bg-surface rounded-md shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto border border-border mx-4"
             @mousedown=${(e: Event) => e.stopPropagation()}
             @click=${(e: Event) => e.stopPropagation()}>
          <div class="sticky top-0 bg-surface flex justify-between items-center px-6 pt-5 pb-4 border-b border-border">
            <h2 class="m-0 font-semibold text-xl text-text">${title}</h2>
            <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          <div class="p-6">${body}</div>
        </div>
      </div>
    `;
  }

  /**
   * 渲染邮箱账户管理页面
   *
   * 页面布局：
   * 1. 顶部工具栏：标题、同步邮件按钮、新增账户按钮、刷新/计数工具栏
   * 2. 表格区域：邮箱账户列表（含加载遮罩、空状态、管理员可见「所有者」列）
   *    - 同步状态和启用状态为可点击标签，点击切换
   *    - 操作列含测试连接、签名管理、编辑、删除按钮
   * 3. 分页组件
   * 4. 账户编辑模态框（条件渲染）：地址、密码（含可见性切换）、POP3/IMAP/SMTP 服务器
   * 5. 签名编辑器模态框（条件渲染）：最大宽度扩展至 max-w-7xl
   *
   * 列表使用 `repeat` 指令优化渲染，以账户 ID 为 key。
   * 管理员可见「所有者」列， colspan 动态调整。
   *
   * @returns Lit TemplateResult
   */
  render() {
    return html`
      <div class="flex flex-col h-full">
        <div class="flex items-center gap-3 mb-3 flex-shrink-0 flex-wrap">
          <span class="text-base font-semibold text-text">邮箱账户管理</span>
          <div class="flex-1"></div>
          <button class="bg-success hover:bg-success-hover text-white px-4 py-1.5 rounded text-sm font-medium transition-colors cursor-pointer flex items-center gap-2" @click=${this.syncEmails}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>同步邮件
          </button>
          <button class="bg-primary hover:bg-primary-hover text-white px-4 py-1.5 rounded text-sm font-medium transition-colors cursor-pointer shadow-sm shadow-primary/20 flex items-center gap-2" @click=${this.openCreateModal}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>新增账户
          </button>
          <crm-table-toolbar .total=${this.total} .label=${'个账户'} .loading=${this.isLoading} @crm-toolbar-refresh=${() => this.loadAccounts()}></crm-table-toolbar>
        </div>

        <div class="flex-1 min-h-0 flex flex-col">
          <div class="bg-surface rounded-md shadow-lg flex flex-col flex-1 min-h-0 overflow-hidden relative">
            <crm-loading-overlay .active=${this.isLoading}></crm-loading-overlay>
            <div class="overflow-auto flex-1 min-h-0">
              <table class="w-full">
                <thead>
                  <tr>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">邮箱地址</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">POP3</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">IMAP</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">SMTP</th>
                    ${this.isAdmin ? html`<th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">所有者</th>` : ''}
                    <th class="px-4 py-2.5 text-center text-xs font-medium text-text-secondary uppercase tracking-wider">同步</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">状态</th>
                    <th class="px-4 py-2.5 text-center text-xs font-medium text-text-secondary uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody>
                  ${!this.isLoading && this.accounts.length === 0
                    ? html`<tr><td colspan=${this.isAdmin ? 8 : 7} class="text-center py-12 text-text-muted">暂无邮箱账户</td></tr>`
                    : repeat(this.accounts, a => a.id, account => html`
                      <tr class="hover:bg-bg/50 transition-colors border-b border-border">
                        <td class="px-4 py-2 text-sm font-medium text-text truncate max-w-[200px]">${account.address}</td>
                        <td class="px-4 py-2 text-sm text-text font-mono truncate max-w-[140px]">${account.pop3 || '-'}</td>
                        <td class="px-4 py-2 text-sm text-text font-mono truncate max-w-[140px]">${account.imap || '-'}</td>
                        <td class="px-4 py-2 text-sm text-text font-mono truncate max-w-[140px]">${account.smtp || '-'}</td>
                        ${this.isAdmin ? html`<td class="px-4 py-2 text-sm text-text">${this.userMap.get(account.uid) || `UID: ${account.uid}`}</td>` : ''}
                        <td class="px-4 py-2 text-center whitespace-nowrap">
                          <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer ${account.sync_enabled ? 'bg-info/10 text-info' : 'bg-error/10 text-error'}"
                                @click=${() => this.toggleAccountSync(account)}>${account.sync_enabled ? '同步中' : '已关闭'}</span>
                        </td>
                        <td class="px-4 py-2 text-center whitespace-nowrap">
                          <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer ${account.status === 1 ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}"
                                @click=${() => this.toggleAccountStatus(account)}>${account.status === 1 ? '启用' : '禁用'}</span>
                        </td>
                        <td class="px-4 py-2 flex gap-1.5 whitespace-nowrap">
                          <button class="text-secondary hover:text-text text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer inline-flex items-center gap-1 ${this.testingId === account.id ? 'opacity-50' : ''}"
                                  ?disabled=${this.testingId === account.id}
                                  @click=${() => this.testConnection(account)}>
                            <svg class="animate-spin w-3 h-3 transition-opacity ${this.testingId === account.id ? 'opacity-100' : 'opacity-0'}" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                            测试
                          </button>
                          <button class="text-secondary hover:text-text text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer"
                            @click=${() => this.openSignatureModal(account.id)}>签名</button>
                          <button class="text-primary hover:text-primary-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.openEditModal(account)}>编辑</button>
                          <button class="text-error hover:text-error-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.deleteAccount(account.id)}>删除</button>
                        </td>
                      </tr>
                    `)}
                </tbody>
              </table>
            </div>
            <crm-pagination
              .current=${this.currentPage}
              .perPage=${this.perPage}
              .total=${this.total}
              @page-change=${(e: CustomEvent) => this.handlePageChange(e.detail.page)}
            ></crm-pagination>
          </div>
        </div>

      </div>

      ${this.showModal ? this.renderModal(this.editingAccount ? '编辑邮箱账户' : '新增邮箱账户', html`
        <form @submit=${this.handleSubmit} class="space-y-4">
          <div>
            <label class="block mb-1.5 text-sm font-medium text-text">邮箱地址 *</label>
            <input type="email" name="address" required class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" .value=${this.editingAccount?.address || ''} />
          </div>
          <div>
            <label class="block mb-1.5 text-sm font-medium text-text">
              密码 ${this.editingAccount ? '' : html`<span class="text-error">*</span>`}
            </label>
            <div class="relative">
              <input
                type=${this.showPassword ? 'text' : 'password'}
                name="password"
                ?required=${!this.editingAccount}
                class="w-full px-3.5 py-2.5 pr-10 border border-border rounded-md text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
                placeholder=${this.editingAccount ? '留空则不变' : ''}
              />
              <button type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-text-muted hover:text-text transition-colors cursor-pointer"
                @click=${() => { this.showPassword = !this.showPassword; }}
                tabindex="-1">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4">
                  ${this.showPassword
                    ? html`<path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>`
                    : html`<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>`}
                </svg>
              </button>
            </div>
            ${this.editingAccount ? html`<p class="mt-1 text-[11px] text-text-muted">填写以修改密码，留空不变</p>` : ''}
          </div>
          <div>
            <label class="block mb-1.5 text-sm font-medium text-text">POP3 服务器</label>
            <input type="text" name="pop3" placeholder="例如: pop.gmail.com:995" class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" .value=${this.editingAccount?.pop3 || ''} />
          </div>
          <div>
            <label class="block mb-1.5 text-sm font-medium text-text">IMAP 服务器</label>
            <input type="text" name="imap" placeholder="例如: imap.gmail.com:993" class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" .value=${this.editingAccount?.imap || ''} />
          </div>
          <div>
            <label class="block mb-1.5 text-sm font-medium text-text">SMTP 服务器</label>
            <input type="text" name="smtp" placeholder="例如: smtp.gmail.com:587" class="w-full px-3.5 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" .value=${this.editingAccount?.smtp || ''} />
          </div>
          <div class="flex justify-end gap-2 mt-6 pt-4 border-t border-border">
            <button type="button" class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer" @click=${this.testModalConnection}>测试连接</button>
            <div class="flex-1"></div>
            <button type="button" class="px-4 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer" @click=${this.closeModal}>取消</button>
            <button type="submit" class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20">保存</button>
          </div>
        </form>
      `, this.closeModal) : ''}

      ${this.signatureModalAccountId !== null ? this.renderModal('管理签名', html`
        <crm-signature-editor .accountId=${this.signatureModalAccountId}
          @crm-signature-saved=${this.closeSignatureModal}
          @crm-signature-cancel=${this.closeSignatureModal}>
        </crm-signature-editor>
      `, this.closeSignatureModal, 'max-w-7xl') : ''}
    `;
  }
}
